<script>
  import Modal from '@components/shared/modal.svelte'
  import { patch } from '@lib/requests'
  import { showAlert } from '@lib/utils'
  
  let { animals = [] } = $props()

  let animal_id = $state(animals[0]?.id)
  let updating = $state(false)
  let editModal = $state(false)
  let refocusFirstTextarea = $state(true)

  let hasTextChanges = $derived(animals.some(animal => hasUnsavedChanges(animal)))

  let modes = [
    { label: "Motif", value: "motive", blank_text: "Aucun motif défini", height: 10 },
    { label: "Examens", value: "exams", blank_text: "Aucun examen défini", height: 10 },
    { label: "Diagnostic", value: "diagnosis", blank_text: "Aucun diagnostic défini", height: 4 },
    { label: "Traitement", value: "treatment", blank_text: "Aucun traitement défini", height: 6 }
  ]

  let editingMode = $state("motive")

  let animal = $derived(animals.find(animal => animal.id === animal_id))
  
  // Local variables for editing in modal - track each animal's editing state separately
  let editingTexts = $state({})
  
  // Current animal's editing texts for binding - individual reactive variables
  let currentMotive = $state("")
  let currentExams = $state("")
  let currentDiagnosis = $state("")
  let currentTreatment = $state("")
  
  // Update current editing texts when animal changes
  $effect(() => {
    if (editingTexts[animal_id]) {
      currentMotive = editingTexts[animal_id].motive || ""
      currentExams = editingTexts[animal_id].exams || ""
      currentDiagnosis = editingTexts[animal_id].diagnosis || ""
      currentTreatment = editingTexts[animal_id].treatment || ""
    }
  })
  
  // Update editingTexts when current values change
  $effect(() => {
    if (editingTexts[animal_id]) {
      editingTexts[animal_id].motive = currentMotive
      editingTexts[animal_id].exams = currentExams
      editingTexts[animal_id].diagnosis = currentDiagnosis
      editingTexts[animal_id].treatment = currentTreatment
    }
  })

  async function submitTexts(e) {
    e.preventDefault()
    updating = true
    
    try {
      // Save all animals' texts
      const promises = animals.map(async (animal) => {
        const animalTexts = editingTexts[animal.id]
        if (!animalTexts) return null
        
        // If animal texts haven't changed, don't save
        if (animal.motive === animalTexts.motive && animal.exams === animalTexts.exams && animal.diagnosis === animalTexts.diagnosis && animal.treatment === animalTexts.treatment) return true
        
        const body = { associated_animal: { ...animalTexts } }
        const response = await patch(`/api/v1/associated_animals/${animal.association_id}`, { body })
        
        if (response.ok) {
          const json = await response.json
          animals = animals.map(a => a.id === animal.id ? { ...a, ...json } : a)
          return true
        } else {
          console.error(`Failed to save texts for animal ${animal.name}`)
          return false
        }
      })
      
      const results = await Promise.all(promises)
      const successCount = results.filter(Boolean).length
      
      if (successCount === animals.length) {
        editingTexts = {} // Clear editing texts after successful save
        editModal = false
        showAlert("Textes mis à jour avec succès", { type: "success" })
      } else if (successCount > 0) {
        editingTexts = {} // Clear editing texts after partial save
        editModal = false
        showAlert(`Textes mis à jour pour ${successCount}/${animals.length} animaux`, { type: "warning" })
      } else {
        showAlert("Erreur lors de la mise à jour des textes", { type: "error" })
      }

    } catch (error) {
      console.error(error)
      showAlert("Erreur lors de la mise à jour des textes", { type: "error" })
    } finally {
      updating = false
    }
  }

  // When changing animal in modal
  function setModalTab(id) {
    animal_id = id
    if (!refocusFirstTextarea) return
    
    const textarea = document.querySelector(`textarea[name="${modes[0].value}"]`)
    textarea?.focus()
    textarea?.select()
    textarea?.scrollIntoView({ behavior: "smooth", block: "center" })
  }

  function openEditModal(mode) {
    editingMode = mode
    // Initialize editing texts for animals that don't already have them
    // This preserves any existing unsaved changes
    animals.forEach(animal => {
      if (!editingTexts[animal.id]) {
        editingTexts[animal.id] = {
          motive: animal.motive || "",
          exams: animal.exams || "",
          diagnosis: animal.diagnosis || "",
          treatment: animal.treatment || ""
        }
      }
    })
    editModal = true
  }

  function hasUnsavedChanges(animal) {
    if (!editingTexts[animal.id]) return false
    return animal.motive !== editingTexts[animal.id].motive || animal.exams !== editingTexts[animal.id].exams || animal.diagnosis !== editingTexts[animal.id].diagnosis || animal.treatment !== editingTexts[animal.id].treatment
  }

  function closeModal() {
    // Clear all editing texts when modal is closed to discard unsaved changes
    editingTexts = {}
    editModal = false
  }

  $effect(() => {
    if (editModal) {
      const textarea = document.querySelector(`textarea[name="${editingMode}"]`)
      if (textarea) {
        textarea.focus()
        textarea.select()
        textarea.scrollIntoView({ behavior: "smooth", block: "center" })
      }
    }
  })

</script>

<div class="content-block">
  
  <!-- Header -->
  <header class:with-tabs={animals.length > 0}>

    {#if animals.length > 0}

      <!-- The animal tabs -->
      <div class="flex justify-between items-center gap-6">

        {#each animals as animal (animal.id)}
          {@const active = animal_id === animal.id}
          <div class="tab" class:active>
            <button type="button" class="text-smaller text-gray-600" onclick={() => animal_id = animal.id}>
              {animal.name}
              {#if animal.icon}
                <i class={`fal fa-${animal.icon} text-gray-400 ml-1 text-xs relative top-[-1px]`}></i>
              {/if}
            </button>
          </div>
        {/each}
        
      </div>

    {:else}
      <h6 class="title">Textes</h6>
    {/if}

  </header>

  <!-- Content -->
  <main class="!py-8">

    {#if animals.length > 0}
    
      <div class="grid grid-cols-2 gap-x-12 gap-y-10">
        {#each modes as mode (mode.value)}
          <section>
            <h6 class="underline text-smaller text-gray-900 italic mb-2">{mode.label}</h6>
            <button type="button" class="help-text text-sm cursor-pointer text-left hover:underline" onclick={() => openEditModal(mode.value)}>
              {#if animal[mode.value]}
                <span>{animal[mode.value]}</span>
              {:else}
                <span class="text-gray-500/80">({mode.blank_text})</span>
              {/if}
            </button>
          </section>
        {/each}
      </div>

    {:else}
      <p class="help-text text-smaller">Aucun animal</p>
    {/if}
  
  </main>

</div>

<Modal bind:open={editModal} width="50rem" onClose={closeModal} closeOnEscape={false} closeOnClickOutside={false}>

  {#snippet tabs()}
    {#each animals as animal (animal.id)}
      <button type="button" class="modal-tab" class:active={animal_id === animal.id} onpointerdown={e => e.preventDefault()} onclick={() => setModalTab(animal.id)}>
        {animal.name}
        {#if animal.icon}
          <i class={`fal fa-${animal.icon} text-gray-400 text-xs ml-1 relative top-[-1px]`}></i>
        {/if}
        {#if hasUnsavedChanges(animal)}
          <span>*</span>
        {/if}
      </button>
    {/each}
  {/snippet}
  
  <form id="animal-texts-form" onsubmit={submitTexts}>
    <div class="form-items">
      <div class="form-item col-span-12">
        <label for="motive">Motif</label>
        <textarea name="motive" id="motive" class="input" bind:value={currentMotive} style="height: 10rem;"></textarea>
      </div>
      <div class="form-item col-span-12">
        <label for="exams">Examens</label>
        <textarea name="exams" id="exams" class="input" bind:value={currentExams} style="height: 10rem;"></textarea>
      </div>
      <div class="form-item col-span-12">
        <label for="diagnosis">Diagnostic</label>
        <textarea name="diagnosis" id="diagnosis" class="input" bind:value={currentDiagnosis} style="height: 4rem;"></textarea>
      </div>
      <div class="form-item col-span-12">
        <label for="treatment">Traitement</label>
        <textarea name="treatment" id="treatment" class="input" bind:value={currentTreatment} style="height: 6rem;"></textarea>
      </div>
    </div>
  </form>

  {#snippet footerButtons()}
    <button type="submit" class="button submit" disabled={updating || !hasTextChanges} form="animal-texts-form">Enregistrer</button>
  {/snippet}
</Modal>